import { Injectable, OnDestroy } from '@angular/core';
import { AlertController, AlertInput } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { ToastService } from './toast-service';
import { TranslateService } from '@ngx-translate/core';
import { TextKeys } from '../i18n/text-keys';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';

@Injectable({
  providedIn: 'root',
})
export class AlertService implements OnDestroy {
  private subscriptions = new Subscription();

  constructor(
    private alertController: AlertController,
    private translateService: TranslateService
  ) {}

  public async showOkCancelWithCheckbox(
    message: string,
    header: string,
    checkboxLabel: string,
    checkedByDefault = true,
    cssClass?: string
  ): Promise<{ confirmed: boolean; checked: boolean }> {
    if (!message) return { confirmed: false, checked: checkedByDefault };

    const alert = await this.alertController.create({
      header,
      message,
      cssClass: ToastService.getCssClass(cssClass),
      inputs: [
        {
          type: 'checkbox',
          label: checkboxLabel,
          value: 'send',
          checked: checkedByDefault,
        },
      ],
      buttons: [
        {
          text: this.translateService.instant(TextKeys.general.okay),
          role: 'confirm',
        },
        {
          text: this.translateService.instant(TextKeys.general.cancel),
          role: 'cancel',
        },
      ],
    });

    await alert.present();
    const { role, data } = await alert.onDidDismiss();

    // Ionic returns selected values for checkboxes as data.values (array)
    const values: string[] = (data && (data.values as string[])) || [];
    const checked = values.includes('send');

    return { confirmed: role === 'confirm', checked };
  }

  public showOkAlert(message: string, header: string, cssClass?: string) {
    if (!message) {
      return;
    }

    this.subscriptions.add(
      fromPromise(
        this.alertController.create({
          message,
          header,
          cssClass: ToastService.getCssClass(cssClass),
          buttons: [
            {
              text: this.translateService.instant(TextKeys.general.okay),
            },
          ],
        })
      ).subscribe((toast) => {
        toast.present();
      })
    );
  }

  public showOkCancelAlert(
    message: string,
    header: string,
    callbackOk: any,
    callbackCancel: any,
    cssClass?: string
  ) {
    if (!message) {
      return;
    }

    this.subscriptions.add(
      fromPromise(
        this.alertController.create({
          message,
          header,
          cssClass: ToastService.getCssClass(cssClass),
          buttons: [
            {
              text: this.translateService.instant(TextKeys.general.okay),
              handler: () => {
                callbackOk();
              },
            },
            {
              text: this.translateService.instant(TextKeys.general.cancel),
              handler: () => {
                if (callbackCancel) callbackCancel();
              },
            },
          ],
        })
      ).subscribe((toast) => {
        toast.present();

        const onKeyPress = (event: KeyboardEvent) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            toast.dismiss();
            callbackOk();
          }
        };

        window.addEventListener('keydown', onKeyPress);
        toast.onDidDismiss().then(() => {
          window.removeEventListener('keydown', onKeyPress);
        });
      })
    );
  }

  public showRadioOkCancelAlert(
    message: string,
    header: string,
    radioInputs: AlertInput[],
    callbackOk: any,
    callbackCancel: any,
    cssClass?: string
  ) {
    if (!message) {
      return;
    }

    this.subscriptions.add(
      fromPromise(
        this.alertController.create({
          message,
          header,
          cssClass: ToastService.getCssClass(cssClass),
          inputs: radioInputs,
          buttons: [
            {
              text: this.translateService.instant(TextKeys.general.okay),
              handler: () => {
                callbackOk();
              },
            },
            {
              text: this.translateService.instant(TextKeys.general.cancel),
              handler: () => {
                if (callbackCancel) callbackCancel();
              },
            },
          ],
        })
      ).subscribe((toast) => {
        toast.present();

        const onKeyPress = (event: KeyboardEvent) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            toast.dismiss();
            callbackOk();
          }
        };

        window.addEventListener('keydown', onKeyPress);
        toast.onDidDismiss().then(() => {
          window.removeEventListener('keydown', onKeyPress);
        });
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
